import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Plus, MapPin, Filter, Heart, Ruler, Info, LogIn, LogOut, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "all", label: "Alle kategorier" },
  { value: "driver", label: "Driver" },
  { value: "wood", label: "Wood" },
  { value: "jern", label: "Jern" },
  { value: "putter", label: "Putter" },
  { value: "bag", label: "Bag" },
  { value: "wedge", label: "Wedge" },
  { value: "hybrid", label: "Hybrid" },
  { value: "tilbehør", label: "Tilbehør" },
  { value: "annet", label: "Annet" },
];

const conditions = [
  { value: "all", label: "Alle tilstander" },
  { value: "ny", label: "Ny" },
  { value: "utmerket", label: "Utmerket" },
  { value: "god", label: "God" },
  { value: "greit", label: "Greit" },
];

const flexOptions = [
  { value: "all", label: "Alle" },
  { value: "ladies", label: "Ladies (L)" },
  { value: "senior", label: "Senior (A)" },
  { value: "regular", label: "Regular (R)" },
  { value: "stiff", label: "Stiff (S)" },
  { value: "x-stiff", label: "Extra Stiff (X)" },
];

const lengthOptions = [
  { value: "all", label: "Alle" },
  { value: "oversized", label: "Oversized (+)" },
  { value: "standard", label: "Standard" },
  { value: "undersized", label: "Undersized (-)" },
];

const handOptions = [
  { value: "all", label: "Alle" },
  { value: "right", label: "Høyre" },
  { value: "left", label: "Venstre" },
];

const shaftMaterialOptions = [
  { value: "all", label: "Alle" },
  { value: "graphite", label: "Graphite" },
  { value: "steel", label: "Steel" },
];

const brandOptions = [
  { value: "all", label: "Alle merker" },
  { value: "callaway", label: "Callaway" },
  { value: "taylormade", label: "TaylorMade" },
  { value: "titleist", label: "Titleist" },
  { value: "ping", label: "Ping" },
  { value: "cobra", label: "Cobra" },
  { value: "mizuno", label: "Mizuno" },
  { value: "cleveland", label: "Cleveland" },
  { value: "srixon", label: "Srixon" },
  { value: "wilson", label: "Wilson" },
  { value: "odyssey", label: "Odyssey" },
  { value: "scotty cameron", label: "Scotty Cameron" },
  { value: "annet", label: "Annet" },
];

// Norwegian cities with coordinates
const norwegianCities = [
  { name: "Oslo", postal_code: "0150", lat: 59.9139, lon: 10.7522 },
  { name: "Bergen", postal_code: "5003", lat: 60.3913, lon: 5.3221 },
  { name: "Trondheim", postal_code: "7030", lat: 63.4305, lon: 10.3951 },
  { name: "Stavanger", postal_code: "4001", lat: 58.9700, lon: 5.7331 },
  { name: "Kristiansand", postal_code: "4600", lat: 58.1599, lon: 7.9959 },
  { name: "Tromsø", postal_code: "9008", lat: 69.6492, lon: 18.9553 },
  { name: "Drammen", postal_code: "3000", lat: 59.7439, lon: 10.2045 },
  { name: "Fredrikstad", postal_code: "1600", lat: 59.2181, lon: 10.9298 },
  { name: "Sandnes", postal_code: "4301", lat: 58.8524, lon: 5.7358 },
  { name: "Bodø", postal_code: "8001", lat: 67.2804, lon: 14.4049 },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCondition, setSelectedCondition] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [showSold, setShowSold] = useState(false);
  // New filter states
  const [selectedFlex, setSelectedFlex] = useState("all");
  const [selectedLength, setSelectedLength] = useState("all");
  const [selectedHand, setSelectedHand] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedShaftMaterial, setSelectedShaftMaterial] = useState("all");
  // Distance-based filter states
  const [userLocation, setUserLocation] = useState("");
  const [maxDistance, setMaxDistance] = useState("");

  useEffect(() => {
    fetchListings();
  }, [selectedCategory, selectedCondition, sortBy, minPrice, maxPrice, showSold, 
      selectedFlex, selectedLength, selectedHand, selectedBrand, selectedShaftMaterial,
      userLocation, maxDistance]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      if (selectedCondition && selectedCondition !== "all") params.append("condition", selectedCondition);
      
      // Add distance-based parameters
      if (userLocation) {
        const city = norwegianCities.find(c => c.name === userLocation);
        if (city) {
          params.append("user_lat", city.lat);
          params.append("user_lon", city.lon);
          if (sortBy === "distance") {
            params.append("sort_by_distance", "true");
          }
          if (maxDistance) {
            params.append("max_distance", maxDistance);
          }
        }
      }
      if (searchTerm) params.append("search", searchTerm);
      if (minPrice) params.append("min_price", minPrice);
      if (maxPrice) params.append("max_price", maxPrice);
      
      // New filters
      if (selectedFlex && selectedFlex !== "all") params.append("flex", selectedFlex);
      if (selectedLength && selectedLength !== "all") params.append("length", selectedLength);
      if (selectedHand && selectedHand !== "all") params.append("hand", selectedHand);
      if (selectedBrand && selectedBrand !== "all") params.append("brand", selectedBrand);
      if (selectedShaftMaterial && selectedShaftMaterial !== "all") params.append("shaft_material", selectedShaftMaterial);
      
      // Set status based on showSold toggle
      params.append("status", showSold ? "sold" : "active");

      let response = await axios.get(`${API}/listings?${params.toString()}`);
      let sortedListings = [...response.data];
      
      // Client-side sorting
      switch (sortBy) {
        case "price-low":
          sortedListings.sort((a, b) => a.price - b.price);
          break;
        case "price-high":
          sortedListings.sort((a, b) => b.price - a.price);
          break;
        case "oldest":
          sortedListings.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          break;
        case "distance":
          // Distance sorting is handled by backend if user location is set
          if (!userLocation) {
            sortedListings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          }
          break;
        case "newest":
        default:
          sortedListings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          break;
      }
      
      setListings(sortedListings);
    } catch (error) {
      console.error("Error fetching listings:", error);
      toast.error("Kunne ikke laste inn annonser");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate("/")}
              data-testid="logo-home-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_golfbazar/artifacts/d3dy0w6h_Skjermbilde%202025-12-20%20125648.png" 
                alt="GolfMarked Logo" 
                className="h-14 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/utstyrsguide")}
                className="hidden sm:flex bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                data-testid="equipment-guide-button"
              >
                <Ruler className="w-4 h-4 mr-2" />
                Utstyrsguide
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/favorites")}
                data-testid="favorites-button"
              >
                <Heart className="w-4 h-4 mr-2" />
                Favoritter
              </Button>
              <Button
                onClick={() => navigate("/create")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="create-listing-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Legg ut annonse
              </Button>
              {isAuthenticated() ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      logout();
                      toast.success("Logget ut");
                    }}
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logg ut
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/login")}
                  data-testid="login-button"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Logg inn
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Usikker på brukt golfutstyr?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Vi hjelper deg å velge riktig før du kjøper.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Vet du hva du leter etter? Søk her – eller bruk guiden."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 bg-white border-gray-300"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700 h-12"
                data-testid="search-button"
              >
                Søk
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="h-12"
                data-testid="filter-toggle-button"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </div>
          </form>

          {showFilters && (
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-testid="filters-section">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Kategori
                  </label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="category-filter">
                      <SelectValue placeholder="Velg kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Tilstand
                  </label>
                  <Select value={selectedCondition} onValueChange={setSelectedCondition}>
                    <SelectTrigger data-testid="condition-filter">
                      <SelectValue placeholder="Velg tilstand" />
                    </SelectTrigger>
                    <SelectContent>
                      {conditions.map((cond) => (
                        <SelectItem key={cond.value} value={cond.value}>
                          {cond.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Min pris (kr)
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    data-testid="min-price-filter"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Maks pris (kr)
                  </label>
                  <Input
                    type="number"
                    placeholder="Ubegrenset"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    data-testid="max-price-filter"
                  />
                </div>
              </div>
              
              {/* Advanced Filters */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Avanserte filter</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Merke
                    </label>
                    <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                      <SelectTrigger data-testid="brand-filter">
                        <SelectValue placeholder="Velg merke" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandOptions.map((brand) => (
                          <SelectItem key={brand.value} value={brand.value}>
                            {brand.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Stivhet/Flex
                    </label>
                    <Select value={selectedFlex} onValueChange={setSelectedFlex}>
                      <SelectTrigger data-testid="flex-filter">
                        <SelectValue placeholder="Velg flex" />
                      </SelectTrigger>
                      <SelectContent>
                        {flexOptions.map((flex) => (
                          <SelectItem key={flex.value} value={flex.value}>
                            {flex.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Lengde
                    </label>
                    <Select value={selectedLength} onValueChange={setSelectedLength}>
                      <SelectTrigger data-testid="length-filter">
                        <SelectValue placeholder="Velg lengde" />
                      </SelectTrigger>
                      <SelectContent>
                        {lengthOptions.map((length) => (
                          <SelectItem key={length.value} value={length.value}>
                            {length.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Hånd
                    </label>
                    <Select value={selectedHand} onValueChange={setSelectedHand}>
                      <SelectTrigger data-testid="hand-filter">
                        <SelectValue placeholder="Velg hånd" />
                      </SelectTrigger>
                      <SelectContent>
                        {handOptions.map((hand) => (
                          <SelectItem key={hand.value} value={hand.value}>
                            {hand.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Shaft-materiale
                    </label>
                    <Select value={selectedShaftMaterial} onValueChange={setSelectedShaftMaterial}>
                      <SelectTrigger data-testid="shaft-material-filter">
                        <SelectValue placeholder="Velg materiale" />
                      </SelectTrigger>
                      <SelectContent>
                        {shaftMaterialOptions.map((material) => (
                          <SelectItem key={material.value} value={material.value}>
                            {material.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Distance-based filtering */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Avstandssøk</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Din lokasjon
                      </label>
                      <Select value={userLocation || "all"} onValueChange={(value) => setUserLocation(value === "all" ? "" : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Velg din by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle lokasjoner</SelectItem>
                          {norwegianCities.map((city) => (
                            <SelectItem key={city.name} value={city.name}>
                              {city.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Maks avstand (km)
                      </label>
                      <Input
                        type="number"
                        placeholder="F.eks. 50"
                        value={maxDistance}
                        onChange={(e) => setMaxDistance(e.target.value)}
                        disabled={!userLocation}
                      />
                    </div>
                  </div>
                  {userLocation && (
                    <p className="text-xs text-gray-500 mt-2">
                      Viser annonser {maxDistance ? `innenfor ${maxDistance} km` : 'sortert etter avstand'} fra {userLocation}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <p className="text-gray-500">Laster annonser...</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg" data-testid="no-listings">
            <p className="text-gray-500 mb-4">Ingen annonser funnet</p>
            <Button
              onClick={() => navigate("/create")}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Bli den første til å legge ut
            </Button>
          </div>
        ) : (
          <>
            {/* Sort and Results Count */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <p className="text-gray-600" data-testid="results-count">
                  {listings.length} annonse{listings.length !== 1 ? 'r' : ''} funnet
                </p>
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-sold"
                    checked={showSold}
                    onCheckedChange={setShowSold}
                    data-testid="show-sold-toggle"
                  />
                  <Label htmlFor="show-sold" className="text-sm text-gray-600 cursor-pointer">
                    Vis solgte
                  </Label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Sorter:</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Nyeste først</SelectItem>
                    <SelectItem value="oldest">Eldste først</SelectItem>
                    <SelectItem value="price_asc">Pris: Lav til høy</SelectItem>
                    <SelectItem value="price_desc">Pris: Høy til lav</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {listings.map((listing) => (
              <Card
                key={listing.id}
                className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white border border-gray-200"
                onClick={() => navigate(`/listing/${listing.id}`)}
                data-testid={`listing-card-${listing.id}`}
              >
                <CardContent className="p-0">
                  <div className="aspect-video bg-gray-200 overflow-hidden rounded-t-lg">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-4xl">⛳</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg text-gray-900 flex-1" data-testid={`listing-title-${listing.id}`}>
                        {listing.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {listing.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-emerald-600" data-testid={`listing-price-${listing.id}`}>
                        {listing.price.toLocaleString('nb-NO')} kr
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1" />
                        {listing.location}
                        {listing.distance && (
                          <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            {listing.distance} km
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {listing.category}
                      </span>
                      <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded">
                        {listing.condition}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          </>
        )}
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              © 2025 GolfMarked.no
            </div>
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => navigate("/club-fitting-guide")}
                className="text-gray-500 hover:text-emerald-600"
              >
                Club Fitting Guide
              </button>
              <button
                onClick={() => navigate("/wedge-grind-guide")}
                className="text-gray-500 hover:text-emerald-600"
              >
                Wedge Grind Guide
              </button>
              <button
                onClick={() => navigate("/admin/login")}
                className="text-gray-400 hover:text-gray-600"
              >
                Admin
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;